<template>
  <div class="screen--landscape">
    <lottie-animation
      path="./img/phone.json"
      :loop="true"
      :autoPlay="true"
      :speed="1"
    />
    <div id="phoneAnimation"></div>
  </div>

  <div
    v-show="props.showLoader"
    class="w-screen h-screen flex flex-row items-center justify-center"
  >
    <CircularLoader :radius="30" :percentage="state.percentage" />
  </div>

  <div
    class="content--orbital_wrapper w-full h-screen absolute top-0 inline-block overflow-hidden root"
    :class="state.zoom > 0 ? 'cursor-grab' : 'cursor-move'"
    @mousedown="handleDragStart"
    @mouseup="handleEndDrag"
    @mousemove="handleDragging"
    @dragleave="handleDragLeave"
    @touchstart="handleTouchStart"
    @touchend="handleTouchEnd"
    @touchmove="handleTouchMove"
    @wheel="handleWheelScroll"
  >
    <div
      v-show="!props.showLoader"
      id="container"
      class="relative block"
      :style="containerStyle"
    >
      <canvas
        id="viewer"
        class="absolute left-1/2 overflow-hidden top-0"
        style="transform: translate(-50%, -50%)"
        width="3840"
        height="2160"
      ></canvas>
      <div
        id="overlay"
        class="absolute top-0 left-1/2"
        style="width: 3840px; height: 2160px; transform: translate(-50%, -50%)"
      >
        <svg v-if="props.name" viewBox="0 0 3840 2160" :id="props.name">
          <template v-for="(floor, index) in state.polygons">
            <template
              v-for="polygon in floor"
              :key="'polygon-' + polygon.name + '-' + index"
            >
              <g>
                <circle
                  v-if="
                    props.building.building_id === null &&
                    props.building.buildings &&
                    props.building.buildings.data.length > 0
                  "
                  :cx="getCenterOfPolygon(polygon.points[state.range]).x"
                  :cy="getCenterOfPolygon(polygon.points[state.range]).y"
                  r="40"
                  :id="`polygon-${polygon.name}`"
                  :class="`${index === props.name ? 'visible' : 'invisible'}`"
                  class="cursor-pointer transition-colors circle stroke-[0.5em]"
                  @mousedown.prevent.stop="handleClickCircle(polygon.name)"
                />
                <polygon
                  v-else
                  :key="'polygon-' + polygon.name + '-' + index"
                  @mouseenter.prevent.stop="handleMouseEnterPolygon(polygon.name, $event)"
                  @mouseleave="handleMouseLeavePolygon"
                  @mousedown.prevent.stop="handleClickPolygon"
                  @touchstart.prevent.stop="handleTouchPolygon(polygon.name, $event)"
                  :points="polygon.points[state.range]"
                  :id="`polygon-${polygon.name}`"
                  class="transition-colors stroke-[0.3em]"
                  :class="`${index === props.name ? 'visible' : 'invisible'} ${
                    props.results
                      .map((floor) => floor.units)
                      .flat()
                      .map((unit) => unit.name).length === 0 ||
                    props.results
                      .map((floor) => floor.units)
                      .flat()
                      .map((unit) => unit.name)
                      .includes(polygon.name)
                      ? ''
                      : 'hidden'
                  } ${
                    getAvailability(polygon.name) !== 7 ? 'cursor-pointer' : ''
                  }`"
                  :style="
                    (props.currentUnit &&
                      props.currentUnit.name === polygon.name &&
                      state.isPolygonActive) ||
                    (props.selectedUnit && props.selectedUnit.name === polygon.name)
                      ? getSelectedPolygonStyle(polygon.name)
                      : getPolygonStyle(polygon.name)
                  "
                  style="transform: skew(-0.5deg, 0deg)"
                />
                <text
                  v-if="
                    props.name !== 'full' &&
                    withTooltip &&
                    getAvailability(polygon.name) === 1 &&
                    withOrbitalUnitName
                  "
                  :x="getCenterOfPolygon(polygon.points[state.range]).x"
                  :y="getCenterOfPolygon(polygon.points[state.range]).y"
                  text-anchor="middle"
                  fill="gray"
                  font-size="30"
                  class="capitalize pointer-events-none select-none "
                  :class="`${index === props.name ? 'visible' : 'invisible'} ${
                    props.results
                      .map((floor) => floor.units)
                      .flat()
                      .map((unit) => unit.name).length === 0 ||
                    props.results
                      .map((floor) => floor.units)
                      .flat()
                      .map((unit) => unit.name)
                      .includes(polygon.name)
                      ? ''
                      : 'hidden'
                  }`"
                >
                  {{ getUnitName(polygon.name) }}
                </text>
                <text
                  v-else-if="props.building.building_id === null"
                  :x="getCenterOfPolygon(polygon.points[state.range]).x"
                  :y="getCenterOfPolygon(polygon.points[state.range]).y - 100"
                  text-anchor="middle"
                  fill="var(--base-color)"
                  font-size="80"
                  class="capitalize pointer-events-none select-none circle-legend"
                  :class="`${index === props.name ? 'visible' : 'invisible'}`"
                >
                  {{ getBuildingDescription(polygon.name) }}
                </text>
              </g>
            </template>
          </template>
        </svg>
      </div>
    </div>
    <div
      v-show="!props.showLoader"
      class="compass--wrapper absolute w-24 top-20"
    >
      <img
        class="compass--img"
        id="compass"
        src="./../../../../../public/img/orbitals/bousole.png"
        alt=""
        :style="`transform: rotate(${state.rotate}deg)`"
      />
      <div class="flex flex-col gap-2 compass--btns">
        <button
          type="button"
          class="btn--main"
          @click.stop="handleAddZoom"
          @mousedown.stop
        >
          +
        </button>
        <button
          type="button"
          class="btn--main"
          @click.stop="handleSubstractZoom"
          @mousedown.stop
        >
          -
        </button>
      </div>
    </div>
    <transition>
      <div
        id="tooltip"
        class="text-center absolute bg-white/[0.8] p-1 flex flex-col items-center justify-center tooltip--info"
        @mouseleave="handleMouseLeaveTooltip"
        @mouseenter.stop="handleMouseEnterTooltip"
        :style="`top:${state.tooltipPosition.y}px;left:${state.tooltipPosition.x}px`"
        v-show="state.isTooltipActive"
      >
        <template v-if="props.currentUnit">
          <p class="ref--number" v-if="withName">
            <template v-if="props.currentUnit.unit">
              {{
                props.currentUnit.unit.fullname
                  ? props.currentUnit.unit.fullname
                  : props.currentUnit.unit.name
              }}
            </template>
            <template v-else>
              {{
                props.currentUnit.fullname ? props.currentUnit.fullname : props.currentUnit.name
              }}
            </template>
          </p>
          <!-- Ajout de la mention Duplex -->
          <p v-if="isDuplex" class="duplex--mention">
            {{ t('Duplex', 'Duplex') }}
          </p>
          <ul v-if="withDetail">
            <li
              v-if="withAvailability"
            >
              <span class="availaibility">
                <template v-if="props.currentUnit.unit">
                  {{
                    props.currentUnit.unit.status
                      ? props.currentUnit.unit.status.translatable.name[locale]
                      : "Inconnu"
                  }}
                  <span
                    class="dot"
                    :style="{
                      'background-color': props.currentUnit.unit.status
                        ? props.currentUnit.unit.status.color
                        : '#eee',
                    }"
                  ></span>
                </template>
                <template v-else>
                  {{ props.currentUnit.status ? props.currentUnit.status.translatable.name[locale] : "Inconnu" }}
                  <span
                    class="dot"
                    :style="{
                      'background-color': props.currentUnit.status
                        ? props.currentUnit.status.color
                        : '#eee',
                    }"
                  ></span>
                </template>
              </span>
            </li>
            <li v-if="withTypology && getAvailability(props.currentUnit.name) === 1">
              <span class="typology">
                <template v-if="PROJECT_TOPOLOGY.labels[props.project.topology] === 'T'">
                  {{ props.currentUnit.bedrooms === 1 ? 'ST' : 'T' + props.currentUnit.bedrooms }}
                </template>
                <template v-else>
                    <template v-if="props.currentUnit.bedrooms > 0">
                      {{ props.currentUnit.bedrooms + ' ch.'}}
                    </template>
                    <template v-else>
                      {{ t('studio', 'studio') }}
                    </template>
                </template>
              </span>
            </li>
            <template v-if="props.currentUnit.unit && props.currentUnit.unit.surface">
              <li>
                {{t('Surface')}}:
                <span class="info--li"> {{ props.currentUnit.unit.surface }}m² </span>
              </li>
            </template>
            <template v-else-if="props.currentUnit.surface">
              <li>
                {{t('Surface')}}:
                <span class="info--li"> {{ props.currentUnit.surface }}m² </span>
              </li>
            </template>
            <li
              v-if="
                props.currentUnit.project &&
                props.currentUnit.project.show_price &&
                props.currentUnit.status.id === 1
              "
            >
              <template v-if="props.currentUnit.unit && props.currentUnit.unit.price">
                {{t('Prix')}}:
                <span class="info--li">
                  {{
                    new Intl.NumberFormat("fr-BE", {
                      style: "currency",
                      currency: "EUR",
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    }).format(props.currentUnit.unit.price)
                  }}
                </span>
                <small v-if="withPriceDetail">{{
                  props.currentUnit.unit.price_detail
                }}</small>
              </template>
              <template v-else-if="props.currentUnit.price">
                {{t('Prix')}}:
                <span class="info--li">
                  {{
                    new Intl.NumberFormat("fr-BE", {
                      style: "currency",
                      currency: "EUR",
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    }).format(props.currentUnit.price)
                  }}
                </span>
                <p>
                  <small v-if="withPriceDetail">{{
                    props.currentUnit.price_detail
                  }}</small>
                </p>
              </template>
            </li>
          </ul>
          <a
            v-if="withDetail && getAvailability(props.currentUnit.name) === 1"
            @mousedown.prevent.stop="
              props.currentUnit.unit
                ? handleShowUnit(props.currentUnit.unit)
                : handleShowUnit()
            "
            class="cursor-pointer text-center btn--see-more"
            >{{t('Voir plus')}}</a
          >
        </template>
        <template v-else>
          {{t('Pas d\'information pour l\'instant')}} ({{ state.currentName }})
        </template>
      </div>
    </transition>

    <div
      v-show="!props.showLoader"
      class="custom--range custom--range-vertical_custom"
    >
      <input
        type="range"
        min="0"
        max="100"
        :value="state.zoom"
        @input="onZoomDebounced"
        @mousedown="handleStartZooming"
        @mouseup="handleEndZooming"
        class=""
      />
    </div>

    <div
      v-show="!props.showLoader"
      class="z-1 absolute invisible sm:visible flex flex-row items-center justify-center bottom-20 custom--range custom--range-horizontal_slider"
    >
      <input
        type="range"
        min="0"
        max="28"
        v-model="state.range"
        @mousedown.stop
        @touchstart.stop
        @input="handleSliderChange"
        class="w-2/4 m-auto"
      />
    </div>
    <div
      v-show="!props.showLoader"
      class="z-1 absolute invisible sm:visible flex flex-row items-center justify-center bottom-20 custom--range custom--range-horizontal_slider"
    >
      <input
        type="range"
        min="0"
        max="28"
        v-model="state.range"
        @mousedown.stop
        @touchstart.stop
        @input="handleSliderChange"
        class="w-2/4 m-auto"
      />
    </div>
  </div>
</template>

<script setup>
import { reactive, onMounted, onBeforeUnmount, computed, watch } from "vue";
import CircularLoader from "./Circular-Loader";
import LottieAnimation from "lottie-vuejs/src/LottieAnimation.vue";
import Axios from "axios";
import PROJECT_TOPOLOGY from "../../enums/project-topology";
import t from "../../composables/Translator.js"

const state = reactive({
  oldRemainingStep: 1,
  width: document.documentElement.clientWidth,
  height: document.documentElement.clientHeight,
  initialScale: 0.35,
  scale: 0.35,
  numberOfFrames: 29,
  frames: {},
  canvas: null,
  loadedImage: 0,
  range: "0",
  zoom: "0",
  rotate: 0,
  isDragging: false,
  isPanning: false,
  x0: 0,
  y0: 0,
  d0: 0,
  frame0: 0,
  panX0: 0,
  panY0: 0,
  tooltipPosition: {
    x: -9999,
    y: -9999,
  },
  polygons: {},
  currentName: null,
  isPolygonActive: false,
  isInsideTooltip: false,
  isZooming: false,
  isZoomGoingForward: false,
  initialRotation: 0,
  touches: 0,
  percentage: 0,
  textPosition: 2160,
  panX: document.width / 2,
  panY: document.height / 2,
  timeout: null,
  isDelaying: false,
  isTooltipActive: false,
  window: window,
  pinchSensitivity: 0.01,
  downloadProgress: {},
  loadedImages: new Set(),
  isPreloading: false,
  panStart: null,
  PROJECT_TOPOLOGY: PROJECT_TOPOLOGY,
})

const props = defineProps({
  framesPath: String,
  framesExtension: String,
  currentPolygonsPath: String,
  name: String,
  units: Object,
  currentUnit: Object,
  results: Array,
  floor: Object,
  building: Object,
  categories: Array,
  project: Object,
  withFilter: Boolean,
  isImagesPending: Boolean,
  selectedUnit: Object,
  showLoader: Boolean,
})

const emit = defineEmits(["showUnit", "setCurrentUnit", "selectBuilding", "setIsPending"])

onMounted(() => {
    initOrbital();
    setTooltipPosition();

    state.panX =
      document.querySelector(".content--orbital_wrapper").getClientRects()[0]
        .width / 2;
    state.panY =
      document.querySelector(".content--orbital_wrapper").getClientRects()[0]
        .height /
        2 +
      100;

    if (window.innerHeight > window.innerWidth) {
      document.querySelector(".screen--landscape").style.display = "block";
      document.querySelector("#phoneAnimation").style.display = "block";
    } else {
      document.querySelector(".screen--landscape").style.display = "none";
      document.querySelector("#phoneAnimation").style.display = "none";
    }

    const found = props.building.settings.data.find((setting) => setting.name === "initial-rotation");

    if (found) {
        state.initialRotation = Number(found.value);
        state.rotate = state.initialRotation;
    }
})

  // Couleurs des typologies ou catégories
const colors = computed(() => {
  if (props.project.show_category) {
    return props.categories.map( (category) => {
      return {
        id: category.id,
        color: category.color,
      };
    });
  } else {
    return props.project.settings.data
      .filter((setting) => /t\d+-color/.test(setting.name))
      .map((setting) => {
        return {
          id: setting.name.split("-")[0],
          color: setting.value,
        };
      });
  }
});

const isDuplex = computed(() => {
  const current = props.currentUnit;
  if (!current) return false;

  // parent d'une autre unité
  if (current.unit) return true;

  const currentId = current.id;
  if (!currentId) return false;

  const allUnits = props.building.floors.data.map(floor => floor.units.data).flat();

  return allUnits.some((u) => {
    if (u.unit_id && u.unit_id === currentId) return true;
    return false;
  });
});

// Rotation sens horaire
const isRotationClockWise = computed(() => {
  const found = props.project.settings.data.find( (setting) => setting.name === "is-rotation-clockwise" );
  return found ? !!Number(found.value) : true;
});

// Affichage des détails
const withDetail = computed(() => {
  const found = props.project.settings.data.find(
    (setting) => setting.name === "with-detail"
  );
  return found ? !!Number(found.value) : true;
});

// Affichage du nom
const withName = computed(() => {
  const found = props.project.settings.data.find(
    (setting) => setting.name === "with-name"
  );
  return found ? !!Number(found.value) : true;
});

// Affichage du tooltip
const withTooltip = computed(() => {
  const found = props.project.settings.data.find(
    (setting) => setting.name === "with-tooltip"
  );
  return found ? !!Number(found.value) : true;
});

// Affichage du nom sur l'orbital
const withOrbitalUnitName = computed(() => {
  const found = props.project.settings.data.find(
    (setting) => setting.name === "with-orbital-unit-name"
  );
  return found ? !!Number(found.value) : true;
});

// Affichage de la disponibilité
const withAvailability = computed(() => {
  const found = props.project.settings.data.find(
    (setting) => setting.name === "with-availability"
  );
  return found ? !!Number(found.value) : true;
});

// Affichage du détail du prix
const withPriceDetail = computed(() => {
  const found = props.project.settings.data.find(
    (setting) => setting.name === "with-price-detail"
  );
  return found ? !!Number(found.value) : true;
});

// Affichage de la typologie
const withTypology = computed(() => {
  const found = props.project.settings.data.find(
    (setting) => setting.name === "with-typology"
  );
  return found ? !!Number(found.value) : true;
});

// Tooltip pour les non-disponibles
const withTooltipForUnavailables = computed(() => {
  const found = props.project.settings.data.find(
    (setting) => setting.name === "with-unavailables-tooltip"
  );
  return found ? !!Number(found.value) : true;
});

const isOrbitalModalEnabled = computed(() => {
  const found = props.project.settings.data.find(
    (setting) => setting.name === "orbital-modal-enabled"
  );
  return found ? !!Number(found.value) : false;
});

const orbitalModalContent = computed(() => {
  const found = props.project.settings.data.find(
    (setting) => setting.name === "orbital-modal-content"
  );
  return found ? found.value : '';
});

import { useStore } from "vuex";

const store = useStore();

const locale = computed(() => {
  return store.getters['config/locale'];
});

// Watch range
watch(() => state.range, () => {
  handleSliderChange();
});

// Watch currentPolygonsPath
watch(() => props.currentPolygonsPath, async () => {
  const response = await fetch(
    `./../../../../../storage/polygons/${props.currentPolygonsPath}/index.json`
  );
  try {
    state.polygons = JSON.parse(await response.text());
  } catch (e) {
    state.polygons = null;
  }

  state.zoom = 0;
  state.range = 0;
  state.panX = document.querySelector(".content--orbital_wrapper").getClientRects()[0].width / 2;
  state.panY = document.querySelector(".content--orbital_wrapper").getClientRects()[0].height / 2;
});

// Watch name
watch(() => props.name, () => {
  preloadFrames();
});

// Watch building
watch(() => props.building, () => {
  const found = props.building.settings.data.find((setting) => setting.name === "initial-rotation");
  if (found) {
    state.initialRotation = Number(found.value);
    state.rotate = state.initialRotation;
  }
});

// Watch framesPath, framesExtensions
watch([() => props.framesPath, () => props.framesExtension], () => {
  if (!state.isPreloading) {
    preloadFrames();
  }
});

// Watch currentUnit
watch(() => props.currentUnit, () => {
  state.tooltipPosition = { x: -9999, y: -9999 };
  setTooltipPosition();
});

// Watch isImagesPending
watch(() => props.isImagesPending, (newVal, oldVal) => {
  if (oldVal === true && newVal === false) {
    setTooltipPosition();
  }
});

const onZoomDebounced = (event) => {
  if (event.target.value === state.zoom) {
    return;
  }
  state.isZoomGoingForward = event.target.value > state.zoom;
  state.zoom = event.target.value;

  if (state.isZoomGoingForward === true) {
    handleZoomChange("zoom-in");
  } else {
    handleZoomChange("zoom-out");
  }
};

const initOrbital = () => {
  fetch(
    "./../../../../../storage/polygons/" +
      props.currentPolygonsPath +
      "/index.json"
  )
    .then((response) => response.text())
    .then((polygons) => {
      try {
        state.polygons = JSON.parse(polygons);
      } catch (e) {
        console.warn(e);
      }
    });

  window.addEventListener("resize", handleResize);
  setResponsiveScale();
  state.canvas = document.querySelector("#viewer");
  state.rotate = state.initialRotation;
  drawFrame();
};

const handleSliderChange = () => {
  drawFrame();
  state.rotate =
    (360 / state.numberOfFrames) *
      (isRotationClockWise.value ? 28 - Number(state.range) : Number(state.range)) +
    state.initialRotation;
};

const drawFrame = () => {
  if (state.canvas) {
    let ctx = state.canvas.getContext("2d");
    ctx.imageSmoothingEnabled = true;
    let img = state.frames[state.range];
    ctx.clearRect(0, 0, state.canvas.width, state.canvas.height);

    if (img) {
      if (img.complete) {
        ctx.drawImage(img, 0, 0, 3820, 2160);
      } else {
        img.onload = () => ctx.drawImage(img, 0, 0, 3820, 2160);
      }
    }
  }
};

const handleResize = () => {
  state.panX =
    document.querySelector(".content--orbital_wrapper").getClientRects()[0]
      .width / 2;
  state.panY =
    document.querySelector(".content--orbital_wrapper").getClientRects()[0]
      .height /
      2 +
    100;

  drawFrame();
  if (window.innerHeight > window.innerWidth) {
    document.querySelector(".screen--landscape").style.display = "block";
    document.querySelector("#phoneAnimation").style.display = "block";
  } else {
    document.querySelector(".screen--landscape").style.display = "none";
    document.querySelector("#phoneAnimation").style.display = "none";
  }

  setResponsiveScale();
};

const preloadFrames = async () => {
        state.isPreloading = true;
        emit("setIsPending", true);
        state.percentage = 0;
        state.loadedImage = 0;
        state.downloadProgress = {};
        state.loadedImages = new Set();

        const promises = [];

        for (let i = 0; i < state.numberOfFrames; i++) {
            const id = String(i).padStart(2, "0");
            const url = `${props.framesPath}/${id}.${props.framesExtension}`;

            const promise = Axios.get(url, {
                responseType: "blob",
                onDownloadProgress: (event) => {
                    if (event.lengthComputable) {
                      const progress = Math.round((event.loaded / event.total) * 100);
                      state.downloadProgress[id] = progress;

                      // Calcul du pourcentage global
                      //const total = Object.values(state.downloadProgress).reduce((a, b) => a + b, 0);
                      //state.percentage = Math.round(total / state.numberOfFrames);
                    }
                },
            })
            .then(response => {
                return new Promise((resolve, reject) => {
                    const blob = response.data;
                    const img = new Image();
                    const objectURL = URL.createObjectURL(blob);

                    img.onload = () => {
                        URL.revokeObjectURL(objectURL);
                        state.frames[i] = img;
                        state.loadedImage++;
                        state.downloadProgress[id] = 100;
                        state.loadedImages.add(id);
                        state.percentage = Math.round((state.loadedImage / state.numberOfFrames) * 100);
                        resolve();
                    };

                    img.onerror = (err) => {
                        URL.revokeObjectURL(objectURL);
                        console.error(`Erreur de chargement image ${id}:`, err);
                        state.loadedImage++;
                        state.loadedImages.add(id);
                        state.percentage = Math.round((state.loadedImage / state.numberOfFrames) * 100);
                        resolve(); // on ne bloque pas sur erreur
                    };

                    img.src = objectURL;
                });
            })
            .catch(error => {
                console.error(`Erreur réseau image ${id}:`, error);
                state.loadedImage++;
                state.loadedImages.add(id);
                state.percentage = Math.round((state.loadedImage / state.numberOfFrames) * 100);
            });

            promises.push(promise);
        }

        Promise.all(promises).then(() => {
            state.percentage = 100;
            state.isPreloading = false;
            emit("setIsPending", false);
            drawFrame();
        });
    };

const easeInOut = (t) => {
      return t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1;
    };

const handleClickPolygon = () => {
  let isAvailable = false;
  let parentUnit = props.currentUnit;

  if (window.innerWidth <= 1100 && withDetail.value) {
    if (props.units.find((unit) => unit.name === state.currentName)) {
      emit(
        "showUnit",
        props.units.find((unit) => unit.name === state.currentName)
      );
    }
  } else {
    if (props.currentUnit && props.currentUnit.unit) {
      isAvailable = Number(props.currentUnit.unit?.status_id) === 1;
      parentUnit = props.currentUnit.unit;
    } else {
      isAvailable = Number(props.currentUnit?.status_id) === 1;
    }

    if (parentUnit && withDetail.value && isAvailable) {
      emit("showUnit", parentUnit);
    }

    handleEndDrag();
  }
};

const handleTouchPolygon = async (name, event) => {
  if (event.touches.length === 1) {
    if (window.innerWidth <= 1100 && withDetail.value) {
      const unit = props.units.find((unit) => unit.name === name);
      let parentUnit = unit;
      let isAvailable = false;

      if (unit && unit.unit) {
        isAvailable = Number(unit.unit?.status_id) === 1;
        parentUnit = unit.unit;
      } else {
        isAvailable = Number(unit?.status_id) === 1;
      }

      if (parentUnit && isAvailable) {
        emit("showUnit", parentUnit);
      }
    }
  }
};

const handleDragStart = (event) => {
      if (!state.isZooming) {
        const orbitalWrapper = document.querySelector(
          ".content--orbital_wrapper"
        );
        orbitalWrapper.classList.add("orbital-wrapper__isDragging");

        if (event.type === "touchstart") {
          state.x0 = event.touches[0].clientX;
          state.y0 = event.touches[0].clientY;
        } else {
          state.x0 = event.clientX;
          state.y0 = event.clientY;
        }

        if (Number(state.zoom) > 0) {
          state.isPanning = true;
        } else {
          state.frame0 = state.range;
          state.isDragging = true;
        }
      }

      state.panX0 = state.panX;
      state.panY0 = state.panY;
    };

const handleDragLeave = () => {
      state.isPanning = false;
    };

const handleEndDrag = () => {
      document
        .querySelector(".content--orbital_wrapper")
        .classList.remove("orbital-wrapper__isDragging");
      state.isDragging = false;
      state.isPanning = false;
    };

const handleDragging = (event) => {
      if (state.isDragging && Number(state.zoom) === 0) {
        let xt = event.clientX;

        if (event.type === "touchmove") {
          xt = event.touches[0].clientX;
        }

        let deltaPixelsX = xt - state.x0;

        let deltaFrame = -1 * Math.floor(deltaPixelsX / state.numberOfFrames);
        let boundedRange = Math.min(
          Math.max(state.frame0 - deltaFrame, 0),
          state.numberOfFrames - 1
        );

        if (Math.abs(deltaPixelsX) > 20) {
          if (boundedRange === 0 && deltaPixelsX < 0) {
            state.range = state.numberOfFrames - 1;
            state.x0 = xt;
            state.frame0 = state.numberOfFrames - 1;
          } else if (boundedRange === state.numberOfFrames - 1) {
            state.range = 0;
            state.x0 = xt;
            state.frame0 = 0;
          } else {
            state.range = boundedRange;
          }
        }
      }

      if (state.isPanning) {
        let xt = event.clientX;
        let yt = event.clientY;

        if (event.type === "touchmove") {
          xt = event.touches[0].clientX;
          yt = event.touches[0].clientY;
        }

        let deltaPixelsX = xt - state.x0;
        let deltaPixelsY = yt - state.y0;

        state.panX = Math.min(
          Math.max(state.panX0 + deltaPixelsX * (1 + state.scale), 0),
          window.innerWidth * (1 + state.scale)
        );
        state.panY = Math.min(
          Math.max(state.panY0 + deltaPixelsY * (1 + state.scale), 0),
          window.innerHeight * (1 + state.scale)
        );
      }
    };

const handleWheelScroll = (event) => {
      if (state.isDragging) {
        return;
      }

      let newZoom = Math.min(
        Math.max(parseInt(state.zoom) - event.deltaY / 25, 0),
        100
      );

      state.isZoomGoingForward = event.wheelDeltaY > 0;
      state.zoom = newZoom;

      if (state.isZoomGoingForward === true) {
        handleZoomChange("zoom-in");
      } else {
        handleZoomChange("zoom-out");
      }
    };

const handleStartZooming = (e) => {
      state.isZooming = true;

      /*this.handleCenteringDuringZoom();*/

      state.isDragging = false;
    };

const handleZoomChange = (direction = "zoom-out") => {
      state.scale = state.initialScale + 0.65 * (state.zoom / 100);
      if (direction === "zoom-out") {
        handleCenteringDuringZoom();
      }
    };

const handleEndZooming = () => {
      state.isZooming = false;

      // this.handleCenteringDuringZoom();
    };

const handleAddZoom = () => {
      state.zoom = Math.min(Math.max(parseInt(state.zoom) + 10, 0), 100);
      handleZoomChange("zoom-in");
    };

const handleSubstractZoom = () => {
      state.zoom = Math.min(Math.max(parseInt(state.zoom) - 10, 0), 100);
      handleZoomChange("zoom-out");
    };

const handleCenteringDuringZoom = () => {
      const center = {
        x: document.querySelector(".content--orbital_wrapper").getClientRects()[0].width / 2,
        y: document.querySelector(".content--orbital_wrapper").getClientRects()[0].height / 2 + 100,
      };

      // Stocke la position de départ au début du dézoom
      if (!state.panStart) {
        state.panStart = { x: state.panX, y: state.panY };
      }

      const zoomPercentage = state.zoom / 100; // 1 = zoom max, 0 = dézoom total

      // Interpolation linéaire
      state.panX = state.panStart.x * zoomPercentage + center.x * (1 - zoomPercentage);
      state.panY = state.panStart.y * zoomPercentage + center.y * (1 - zoomPercentage);

      // Reset à la fin du dézoom
      if (zoomPercentage === 0) {
        state.panStart = null;
      }
    };

const handlePinch = (event) => {
      /*                if(!this.isDragging) {
                                let a = event.touches[0].clientX - event.touches[1].clientX;
                                let b = event.touches[0].clientY - event.touches[1].clientY;

            /!*                    let dt = Math.sqrt( a*a + b*b )*!/
                                let dt = Math.sqrt(Math.pow(a, 2) + Math.pow(b, 2))

                                this.zoom = Math.ceil(Math.min(Math.max(parseInt(this.zoom) - ((-1*(dt-this.d0)) / 75), 0), 100));

                                this.handleZoomChange()
                            }*/

      if (!state.isDragging && event.touches.length === 2) {
        const touch1 = event.touches[0];
        const touch2 = event.touches[1];

        const dx = touch2.clientX - touch1.clientX;
        const dy = touch2.clientY - touch1.clientY;

        const distance = Math.sqrt(Math.pow(dx, 2) + Math.pow(dy, 2)); // Distance actuelle entre les doigts

        if (state.d0 === 0) {
          state.d0 = distance; // Définir la distance initiale au début du pincement
        } else {
          const zoomChange = (distance - state.d0) / state.d0; // Calculer le pourcentage de changement

          // console.warn(zoomChange);

          if (Math.abs(zoomChange) >= 0.1) {
            // Ajuster la valeur de zoom entre 0 et 100 en fonction du pourcentage de changement
            const zoomDelta = zoomChange * state.pinchSensitivity * 100;
            state.zoom = Math.min(
              Math.max(parseInt(state.zoom) + zoomDelta, 0),
              100
            );

            handleZoomChange("zoom-in");
          }
        }
      }
    };

const handleTouchStart = (event) => {
      if (event.touches.length === 1) {
        handleDragStart(event);
      } else if (event.touches.length === 2) {
/*        let a = event.touches[0].clientX - event.touches[1].clientX;
        let b = event.touches[0].clientY - event.touches[1].clientY;

        this.d0 = Math.sqrt(a * a + b * b);
        this.isDragging = false;
        this.isZooming = true;*/
      }
    };

const handleTouchEnd = (event) => {
      if (event.touches.length === 0) {
        handleEndZooming();
        handleEndDrag();
      }
    };

const handleTouchMove = (event) => {
      if (event.touches.length === 1) {
        handleDragging(event);
      } else if (event.touches.length === 2) {
        /*this.handlePinch(event)*/
      }
    };

const delay = (ms) => {
      return new Promise((res) => (state.timeout = setTimeout(res, ms)));
    };

const handleMouseEnterPolygon = async (name) => {
      let unit = props.units.find((unit) => unit.name === name);

      if (state.isDelaying) {
        await delay(250);
      }

      clearTimeout(state.timeout);
      state.isPolygonActive = true;

      if (window.innerWidth >= 1100) {
        emit("setCurrentUnit", { unit: unit, refresh: false });
        state.isTooltipActive = Number(unit?.status_id) !== 2 || state.withTooltipForUnavailables;
        state.currentName = name;
      }
    };

const handleMouseLeavePolygon = async (event) => {
      clearTimeout(state.timeout);
      state.isDelaying = true;
      await delay(250).then(() => (state.isDelaying = false));

      if (
        event.relatedTarget &&
        event.relatedTarget.getAttribute("id") !== "tooltip" &&
        !state.isInsideTooltip
      ) {
        state.isTooltipActive = false;
        state.isPolygonActive = false;
      }
    };

const handleMouseEnterTooltip = () => {
      state.isInsideTooltip = true;
    };

const handleMouseLeaveTooltip = async () => {
      clearTimeout(state.timeout);
      state.isDelaying = true;
      await delay(250).then(() => (state.isDelaying = false));

      state.isPolygonActive = false;
      state.isInsideTooltip = false;
      state.isTooltipActive = false;
    };

const setTooltipPosition = async () => {
      clearTimeout(state.timeout);
      state.isDelaying = true;
      await delay(250).then(() => (state.isDelaying = false));

      let $polygon = document.querySelector(
        `#polygon-${props.currentUnit ? props.currentUnit.name : "none"}`
      );

      if (!$polygon || $polygon.getBoundingClientRect().left === 0) {
        await delay(250);
        $polygon = document.querySelector(
          `#polygon-${props.currentUnit ? props.currentUnit.name : "none"}`
        );
      }

      if ($polygon && window.innerWidth >= 1100) {
        let $wrapper = document.querySelector(".content--orbital_wrapper");

        let posX =
          Math.abs($polygon.getBoundingClientRect().left) > 100
            ? $polygon.getBoundingClientRect().left
            : 0;
        let posY =
          Math.abs($polygon.getBoundingClientRect().top) > 100
            ? $polygon.getBoundingClientRect().top
            : 0;

        const tooltipHeight =
          document.querySelector("#tooltip").getBoundingClientRect().height > 0
            ? document.querySelector("#tooltip").getBoundingClientRect().height
            : 165;

        state.tooltipPosition = {
          x:
            $wrapper.getBoundingClientRect().width >= 1600
              ? posX -
                (window.screen.width - $wrapper.getBoundingClientRect().width) /
                  2
              : posX,
          y: posY - tooltipHeight,
        };
      }
    };

const handleShowUnit = (unit = props.currentUnit) => {
      handleEndDrag();
      emit("showUnit", unit);
    };

const getTopologyColor = (name) => {
      let unit = props.units.filter((unit) => unit.name === name)[0];

      let isAvailable = false;
      let parentUnit = unit;

      if (unit && unit.unit) {
        isAvailable = Number(unit.unit?.status_id) === 1;
        parentUnit = unit.unit;
      } else {
        isAvailable = unit && Number(unit?.status_id) === 1;
      }

      if (isAvailable) {
        if (props.project.show_category) {
          if (
            colors.value.find(
              (color) => Number(color.id) === Number(parentUnit.category_id)
            )
          ) {
            return colors.value.find(
              (color) => Number(color.id) === Number(parentUnit.category_id)
            ).color;
          }
        } else if (Number(parentUnit.bedrooms) >= 0) {
          if (
            colors.value.find((color) => color.id === "t" + parentUnit.bedrooms)
          ) {
            return colors.value.find(
              (color) => color.id === "t" + parentUnit.bedrooms
            ).color;
          }
        }
      }
    };

const getSelectedPolygonStyle = (name) => {
      let result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(
        getTopologyColor(name)
      );

      let unit = props.units.filter((unit) => unit.name === name)[0];

      let rgb = result
        ? {
            r: parseInt(result[1], 16),
            g: parseInt(result[2], 16),
            b: parseInt(result[3], 16),
          }
        : null;

      if (rgb) {
        return {
          fill: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${
            Number(unit?.status_id) !== 2 ? 1 : 0.35
          })`,
          stroke: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${
            Number(unit?.status_id) !== 2 ? 0.75 : 0.5
          })`,
        };
      }

      return {
        fill: `rgba(135, 135, 135, 0.5)`,
        stroke: `rgba(90, 90, 90, 0.75)`,
      };
    };

const getPolygonStyle = (name) => {
      let result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(
        getTopologyColor(name)
      );

      let rgb = result
        ? {
            r: parseInt(result[1], 16),
            g: parseInt(result[2], 16),
            b: parseInt(result[3], 16),
          }
        : null;

      if (rgb) {
        return {
          fill: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.5)`,
          stroke: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.75)`,
        };
      }

      return {
        fill: `rgba(167, 169, 172, 0.5)`,
        stroke: `rgba(167, 169, 172, 0.75)`,
      };
    };

const getCenterOfPolygon = (points) => {
      let xCoords = points.split(" ").map((point) => point.split(",")[0]);
      let yCoords = points.split(" ").map((point) => point.split(",")[1]);

      return {
        x: (Math.min(...xCoords) + Math.max(...xCoords)) / 2,
        y: (Math.min(...yCoords) + Math.max(...yCoords)) / 2,
      };
    };

const handleClickCircle = (building_name) => {
      // console.warn(building_name);
      emit("selectBuilding", building_name);
    };

const getBuildingDescription = (name) => {
      if (
        props.project.buildings &&
        props.project.buildings.data.find(
          (building) => building.name.toLowerCase() === name.toLowerCase()
        )
      ) {
        return props.project.buildings.data.find(
          (building) => building.name.toLowerCase() === name.toLowerCase()
        ).description;
      }

      return "BAT";
    };

const getAvailability = (name) => {
      let unit = props.units.filter((unit) => unit.name === name)[0];

      if (unit && unit.unit) {
        return Number(unit.unit?.status_id);
      }

      if (unit) {
        return Number(unit?.status_id);
      }

      return 2;
    };

const getUnitName = (name) => {
      let unit = props.units.filter((unit) => unit.name === name)[0];

      if (unit) {
        return unit.translatable.fullname[locale.value] !== "" ? unit.translatable.fullname[locale.value] : unit.name;
      }

      return name;
    };

const setResponsiveScale = () => {
      if (window.innerWidth > 1400) {
        state.scale = 0.35;
        state.initialScale = 0.35;
      }

      if (window.innerWidth <= 1250) {
        state.scale = 0.3;
        state.initialScale = 0.3;
      }

      if (window.innerWidth <= 1180) {
        state.scale = 0.28;
        state.initialScale = 0.28;
      }

      if (window.innerWidth <= 1024) {
        state.scale = 0.25;
        state.initialScale = 0.25;
      }

      if (window.innerWidth <= 950) {
        state.scale = 0.19;
        state.initialScale = 0.19;
      }

      if (window.innerWidth <= 860) {
        state.scale = 0.18;
        state.initialScale = 0.18;
      }

      if (window.innerWidth <= 720) {
        state.scale = 0.165;
        state.initialScale = 0.165;
      }

      if (window.innerWidth <= 600) {
        state.scale = 0.14;
        state.initialScale = 0.14;
      }
};

// Computed pour la position responsive
const containerTransform = computed(() => {
  const isLargeScreen = window.innerWidth >= 1100;
  const translateY = isLargeScreen ? '-40%' : '-55%';
  return `translate3d(-50%, ${translateY}, 0) scale(${state.scale})`;
});

const containerStyle = computed(() => {
  const transition = !state.isPanning ? 'transition: all 0.3s ease' : '';
  return `${transition}; transform: ${containerTransform.value}; left:${state.panX}px; top:${state.panY}px; height:100vh; width:100%`;
});

onBeforeUnmount(() => {
    state.polygons = null;
});
</script>

<style>
.vRange {
  writing-mode: bt-lr;
  writing-mode: vertical-lr;
  direction: rtl
}
.circle {
  stroke: var(--base-color);
  fill: var(--base-color);
  fill-opacity: 0.85;
  transition: transform 0.1s ease-out;
  transform-origin: center;
  transform-box: fill-box;
  transform: skew(-0.5deg, 0deg);
}
.circle:hover {
  transform: scale(1.25) skew(-0.5deg, 0deg);
  transform-origin: center;
  transform-box: fill-box;
}
.circle-legend {
  opacity: 0;
  transition: opacity 0.1s ease;
  fill: var(--base-color);
  filter: drop-shadow(3px 3px 2px rgba(0, 0, 0, .7));
}
.circle:hover ~ .circle-legend {
  opacity: 1;
}
.v-enter-active,
.v-leave-active {
  transition: opacity 0.1s ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}
</style>
